import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Check, X, Eye, EyeOff, Users as UsersIcon } from "lucide-react";
import { listarSetores, criarSetor, atualizarSetor, excluirSetor, Setor } from "../services/setores";
import { useConfirm } from "../components/ConfirmProvider";
import { useAuth } from "../hooks/useAuth";

// Catálogo de setores/equipes — gestor/admin cria/edita; usado no cadastro de
// usuário (dropdown) e pra agrupar a Produtividade.
export function Setores() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { can } = useAuth();
  if (!can(["gestor", "admin"])) return <Navigate to="/" replace />;
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);

  const { data: setores = [], isLoading } = useQuery({
    queryKey: ["setores", "todos"],
    queryFn: () => listarSetores({ todos: true }),
  });

  const criarM = useMutation({
    mutationFn: criarSetor,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["setores"] }); setNovo(false); },
  });
  const atualizarM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) => atualizarSetor(id, dados),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["setores"] }); qc.invalidateQueries({ queryKey: ["usuarios"] }); setEditandoId(null); },
    onError: (e: any) => confirm({ title: "Não foi possível salvar", message: e?.response?.data?.error?.message || "Erro", confirmText: "OK" }),
  });
  const excluirM = useMutation({
    mutationFn: excluirSetor,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["setores"] }),
    onError: (e: any) => confirm({ title: "Não foi possível excluir", message: e?.response?.data?.error?.message || "Erro", confirmText: "OK" }),
  });

  async function aoExcluir(s: Setor) {
    const ok = await confirm({
      title: `Excluir o setor "${s.nome}"?`,
      message: "Se houver pessoas nesse setor, a exclusão é bloqueada — mova-as ou desative o setor.",
      danger: true,
      confirmText: "Excluir",
    });
    if (ok) excluirM.mutate(s.id);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <UsersIcon size={20} /> Setores da equipe
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Divisões da equipe (ex.: Produção, Atendimento, Gestão). Cada pessoa recebe um setor no cadastro de usuário — e a Produtividade compara e premia por setor.
          </p>
        </div>
        <button onClick={() => setNovo(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
          <Plus size={16} /> Novo setor
        </button>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Cor</th>
              <th className="text-left px-4 py-2 font-medium">Setor</th>
              <th className="text-left px-4 py-2 font-medium">Ordem</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody className="text-slate-700 dark:text-slate-200">
            {novo && (
              <LinhaEditavel setor={null} onSalvar={(d) => criarM.mutate(d)} onCancelar={() => setNovo(false)} salvando={criarM.isPending} />
            )}
            {isLoading && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>}
            {!isLoading && setores.length === 0 && !novo && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Nenhum setor cadastrado. Crie o primeiro.</td></tr>
            )}
            {setores.map((s) =>
              editandoId === s.id ? (
                <LinhaEditavel key={s.id} setor={s} onSalvar={(d) => atualizarM.mutate({ id: s.id, dados: d })} onCancelar={() => setEditandoId(null)} salvando={atualizarM.isPending} />
              ) : (
                <tr key={s.id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-2"><span className="inline-block w-5 h-5 rounded" style={{ background: s.cor }} /></td>
                  <td className="px-4 py-2 font-medium">{s.nome}</td>
                  <td className="px-4 py-2 tabular-nums">{s.ordem}</td>
                  <td className="px-4 py-2">
                    {s.ativo
                      ? <span className="text-[11px] uppercase font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">Ativo</span>
                      : <span className="text-[11px] uppercase font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">Inativo</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => atualizarM.mutate({ id: s.id, dados: { ativo: !s.ativo } })} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title={s.ativo ? "Desativar" : "Ativar"}>
                      {s.ativo ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button onClick={() => setEditandoId(s.id)} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Editar">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => aoExcluir(s)} className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded" title="Excluir">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LinhaEditavel({ setor, onSalvar, onCancelar, salvando }: {
  setor: Setor | null;
  onSalvar: (dados: { nome: string; cor: string; ordem: number; ativo: boolean }) => void;
  onCancelar: () => void;
  salvando: boolean;
}) {
  const [nome, setNome] = useState(setor?.nome || "");
  const [cor, setCor] = useState(setor?.cor || "#64748b");
  const [ordem, setOrdem] = useState(setor?.ordem ?? 0);
  const [ativo, setAtivo] = useState(setor?.ativo ?? true);

  function salvar() {
    if (!nome.trim()) return;
    onSalvar({ nome: nome.trim(), cor, ordem: Number(ordem) || 0, ativo });
  }

  return (
    <tr className="border-t border-slate-100 dark:border-slate-800 bg-blue-50/40 dark:bg-blue-900/20">
      <td className="px-4 py-2">
        <input type="color" value={cor} onChange={(e) => setCor(e.target.value)} className="w-8 h-7 rounded cursor-pointer border border-slate-300" title="Cor" />
      </td>
      <td className="px-4 py-2">
        <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Produção" className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" autoFocus />
      </td>
      <td className="px-4 py-2">
        <input type="number" value={ordem} onChange={(e) => setOrdem(Number(e.target.value) || 0)} className="w-20 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" />
      </td>
      <td className="px-4 py-2">
        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} /> Ativo
        </label>
      </td>
      <td className="px-4 py-2 text-right">
        <button onClick={salvar} disabled={!nome.trim() || salvando} className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded disabled:opacity-40" title="Salvar">
          <Check size={14} />
        </button>
        <button onClick={onCancelar} className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Cancelar">
          <X size={14} />
        </button>
      </td>
    </tr>
  );
}
